import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import Bowser from "bowser";

import { oauthApp } from "@/config/oauth-app";
import { verifyCredentials } from "@/contexts/auth/api";
import { useAuth } from "@/contexts/auth/useAuth";
import { authScopes } from "@/contexts/auth/utils";
import { useApi } from "@/hooks";

import type { ApiError } from "@/types/api";
import type { DeliveryMethod } from "@/types/delivery-method";

interface Variables {
  username: string;
  password?: string;
  shouldRedirect?: boolean;
  challengeId?: string;
  securityCode?: string;
}

interface Response {
  access_token: string;
  token_type: "Bearer";
  scope: typeof authScopes;
  created_at: number;
}

interface ErrorResponse extends ApiError {
  error_description: string;
  mfa_token?: string;
  supported_challenge_types?: string;
  supported_delivery_methods?: DeliveryMethod[];
  challenge_id?: string;
}

function useCreateSession() {
  const api = useApi();

  const { addAccount, switchAccount } = useAuth();

  const params = {
    client_id: oauthApp.client_id,
    client_secret: oauthApp.client_secret,
    redirect_uri: oauthApp.redirect_uri,
    grant_type: "password",
    scope: authScopes,
  };

  return useMutation({
    mutationFn: async (variables: Variables) => {
      const browser = Bowser.getParser(window.navigator.userAgent);

      const isVerifyingSecurityCode =
        variables.challengeId && variables.securityCode;
      const url = isVerifyingSecurityCode
        ? "/oauth/v2/verify_security_code"
        : "/oauth/v2/token";

      const { data } = await api.post<Response>(
        url,
        {
          ...params,
          username: variables.username,
          password: variables.password,
          challenge_id: variables.challengeId,
          security_code: variables.securityCode,
        },
        {
          headers: {
            Authorization: null,
            Browser: browser.getBrowserName(),
            OS: browser.getOSName(),
          },
        },
      );

      const account = await verifyCredentials(data.access_token);
      addAccount(data.access_token, account, {
        client_id: params.client_id,
        client_secret: params.client_secret,
      });
      switchAccount(account, { shouldRedirect: variables.shouldRedirect });

      return account;
    },
    onError(_error: AxiosError<ErrorResponse>) {},
  });
}

export { useCreateSession };
